import React, { useState } from "react";
import { AnimatePresence, motion, useReducedMotion } from "framer-motion";
import { ConfiguratorHeader } from "../components/ConfiguratorHeader";
import { ConfiguratorFooter } from "../components/ConfiguratorFooter";
import { OrderSummary } from "../components/OrderSummary";
import { QuoteSuccess } from "../components/QuoteSuccess";
import { ProductStep } from "../components/steps/ProductStep";
import { StudioStep } from "../components/studio/StudioStep";
import { QuantityStep } from "../components/steps/QuantityStep";
import { SendStep } from "../components/steps/SendStep";
import { STEPS, useConfigurator } from "../hooks/useConfigurator";
import type { ContactForm } from "../types/configurator";
type SendStatus = {
  kind: "idle";
} | {
  kind: "sending";
} | {
  kind: "sent";
  reference: string;
  email: string;
};
export function Configurator() {
  const config = useConfigurator();
  const reduceMotion = useReducedMotion();
  const [status, setStatus] = useState<SendStatus>({
    kind: "idle"
  });
  const {
    step,
    product,
    category
  } = config;
  function handleSubmit(form: ContactForm) {
    setStatus({
      kind: "sending"
    });
    window.setTimeout(() => {
      const reference = `AFF-${new Date().getFullYear()}-${Math.floor(10000 + Math.random() * 89999)}`;
      setStatus({
        kind: "sent",
        reference,
        email: form.email
      });
    }, 900);
  }
  function restart() {
    config.reset();
    setStatus({
      kind: "idle"
    });
    window.scrollTo({
      top: 0
    });
  }
  function navigate(fn: () => void) {
    fn();
    window.scrollTo({
      top: 0
    });
  }
  const showSummary = !!product && step === 2;
  const hint = category ? "Choisissez un modèle pour continuer." : "Choisissez une catégorie pour commencer.";
  const viewKey = `${step}-${step === 0 ? category?.id ?? "all" : ""}`;
  if (status.kind === "sent") {
    return <div className="flex min-h-full w-full flex-col bg-paper text-ink" data-id="element-351" data-mp="1om817y51eya8">
        <ConfiguratorHeader step={STEPS.length - 1} maxReached={-1} onStepClick={() => undefined} onQuit={restart} data-id="element-352" data-mp="1klefnbf2hvki" />
        <main className="flex-1 px-5 md:px-12" data-id="element-353" data-mp="hb0iehvs9xxi">
          <QuoteSuccess reference={status.reference} email={status.email} onRestart={restart} data-id="element-354" data-mp="mad77867tf2q" />
        </main>
      </div>;
  }
  return <div className="flex min-h-full w-full flex-col bg-paper font-sans text-ink" data-id="element-355" data-mp="rb160g0or07i">
      <ConfiguratorHeader step={step} maxReached={config.maxReached} onStepClick={i => navigate(() => config.goTo(i))} onQuit={restart} data-id="element-356" data-mp="va9zrkvq6y32" />

      <main className="mx-auto w-full max-w-[1400px] flex-1 px-5 pb-16 pt-10 md:px-12 md:pt-16" data-id="element-357" data-mp="3uoc1849mmqdl">
        <div className={showSummary ? "grid gap-10 lg:grid-cols-[1fr_300px]" : ""} data-id="element-358" data-mp="3i16epv7mlup">
          <AnimatePresence mode="wait" initial={false} data-id="element-359" data-mp="7k5tt6v2em8w">
            <motion.div key={viewKey} initial={reduceMotion ? false : {
            opacity: 0,
            y: 8
          }} animate={{
            opacity: 1,
            y: 0
          }} exit={reduceMotion ? {
            opacity: 0
          } : {
            opacity: 0,
            y: -4
          }} transition={{
            duration: 0.2,
            ease: [0.23, 1, 0.32, 1]
          }} className="min-w-0" data-id="element-360" data-mp="ccz40a9aymsd">
              {step === 0 && <ProductStep config={config} data-id="element-361" data-mp="33iwynaipjp5w" />}
              {step === 1 && <StudioStep config={config} data-id="element-362" data-mp="33jg4hsv32n3k" />}
              {step === 2 && <QuantityStep config={config} data-id="element-363" data-mp="2zgtk2d0qs4ge" />}
              {step === 3 && <SendStep config={config} sending={status.kind === "sending"} onSubmit={handleSubmit} data-id="element-364" data-mp="2zjfpo742o9vz" />}
            </motion.div>
          </AnimatePresence>
          {showSummary && <div className="hidden lg:block" data-id="element-365" data-mp="2pkgh0ojb4e8z">
              <div className="sticky top-[84px]" data-id="element-366" data-mp="2krx4luwcvp5r">
                <OrderSummary config={config} data-id="element-367" data-mp="2kr44vdvtdzx4" />
              </div>
            </div>}
        </div>
      </main>

      <ConfiguratorFooter isFirst={step === 0 && !category} isLast={step === STEPS.length - 1} canContinue={config.canContinue} quote={step === 0 && !product ? null : config.quote} quantity={config.state.quantity} hint={hint} onBack={() => navigate(config.back)} onNext={() => navigate(config.next)} data-id="element-368" data-mp="2fqzmfibciv49" />
    </div>;
}